/**
 * The last change the tenant change channel (D35) announced for each
 * resource, for the views that edit one: `useLiveResource` asks it who
 * changed the thing on screen, and learns of a delete from it (a deleted
 * resource has no newer version to fetch).
 *
 * A tiny external store (`useSyncExternalStore`): the global change hook
 * (`api/changes.ts`) publishes into it, views read `last(kind, id)`.
 */
import type { ResourceChange, ResourceKind } from '@acp/api-contract/changes.js';

/** A change as heard: the frame's payload and when the server sent it (ms). */
export type HeardChange = { readonly change: ResourceChange; readonly atMs: number };

export type ChangeLog = {
  readonly publish: (change: ResourceChange, atMs: number) => void;
  /** The last change heard for the resource, or null. Stable between publishes (safe as a snapshot). */
  readonly last: (kind: ResourceKind, id: string) => HeardChange | null;
  readonly subscribe: (listener: () => void) => () => void;
};

export function createChangeLog(): ChangeLog {
  const byResource = new Map<string, HeardChange>();
  const listeners = new Set<() => void>();
  const key = (kind: ResourceKind, id: string): string => `${kind}\u0000${id}`;
  return {
    publish(change, atMs) {
      byResource.set(key(change.kind, change.id), { change, atMs });
      for (const listener of listeners) listener();
    },
    last: (kind, id) => byResource.get(key(kind, id)) ?? null,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
